import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { Form } from '@/common/form/Form';
import { FormFields } from '@/common/form/rendering/FormFields';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

// Bulk-creates "<ENTITY>_REL" rows (e.g. PROD_REL) between a fixed parent/child Entity and picked Entities of
// that same type, deriving the rel entity code and fk field names from the entity code and the fixed role
export const RelCreatorDialog = ({ data, closeDialog }) => {
  const { module, entity, parentObj, childObj, fieldDefaults, buildCode, augDialogRowsReq, augDialogFieldDefs } = data;

  const rsrc = useRsrc();
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [existingPickedIds, setExistingPickedIds] = useState();
  const [relFormFieldDefs, setRelFormFieldDefs] = useState();
  const formRef = useRef();

  const fixedObj = parentObj ?? childObj;
  const fixedRole = parentObj ? 'parent' : 'child';
  const pickedRole = fixedRole === 'parent' ? 'child' : 'parent';
  const entityUpperCamel = StringUtils.toUpperCamel(entity);
  const relEntity = `${entity}_REL`;
  const fixedFkField = `${fixedRole}${entityUpperCamel}Id`;
  const pickedFkField = `${pickedRole}${entityUpperCamel}Id`;

  useEffect(() => {
    const rowsReq = { expr: ExprUtils.whereParams(fixedFkField, fixedObj.id) };
    rsrc.rows(module, relEntity, rowsReq).then((rows) => {
      setExistingPickedIds(rows.map((row) => row[pickedFkField]));
    });
  }, []);

  // Any rel fields not already covered by fieldDefaults are collected via a form above the picker
  useEffect(() => {
    rsrc.fieldDefs(module, relEntity).then((fieldDefs) => {
      const fieldToFieldDef = Object.fromEntries(
        fieldDefs.filter((fieldDef) => fieldDef.editLevel !== 'NEVER').map((fieldDef) => [fieldDef.field, fieldDef]),
      );

      [fixedFkField, pickedFkField, 'code', ...Object.keys(fieldDefaults ?? {})].forEach(
        (key) => delete fieldToFieldDef[key],
      );
      GridConstants.removeAuditFieldDefs(fieldToFieldDef);

      setRelFormFieldDefs(Object.values(fieldToFieldDef).sort((a, b) => (a.precedence || 0) - (b.precedence || 0)));
    });
  }, []);

  const augFieldDefs = (fieldToFieldDef) => {
    fieldToFieldDef.checkbox = GridConstants.checkbox;
    GridConstants.removeAuditFieldDefs(fieldToFieldDef);
    augDialogFieldDefs?.(fieldToFieldDef);
  };

  const augRowsReq = useCallback(
    (rowsReq) => {
      const addlExpr = ExprUtils.and(
        ExprUtils.isNot('id', fixedObj.id),
        existingPickedIds.length > 0 ? ExprUtils.isNotOneOf('id', existingPickedIds) : null,
      );
      rowsReq.expr = ExprUtils.and(rowsReq.expr, addlExpr);
      augDialogRowsReq?.(rowsReq);
    },
    [fixedObj.id, existingPickedIds],
  );

  const rowSelected = useCallback((event) => {
    setSelectedEntities(event.api.getSelectedRows());
  }, []);

  const submit = () => {
    const formValues = formRef.current?.formValues ?? {};

    const rels = selectedEntities.map((picked) => {
      const parentCode = fixedRole === 'parent' ? fixedObj.code : picked.code;
      const childCode = fixedRole === 'parent' ? picked.code : fixedObj.code;

      return {
        code: buildCode ? buildCode(fixedObj, picked) : `${parentCode}_REL_${childCode}`,
        [fixedFkField]: fixedObj.id,
        [pickedFkField]: picked.id,
        ...fieldDefaults,
        ...formValues,
      };
    });

    rsrc.save(module, relEntity, rels).then(() => closeDialog(true));
  };

  return (
    <Dialog onClose={() => closeDialog()} open={true} fullWidth maxWidth="lg">
      <DialogTitle>Select {entity}</DialogTitle>
      <DialogContent>
        {relFormFieldDefs?.length > 0 && (
          <Form ref={formRef}>
            <FormFields fieldDefs={relFormFieldDefs} />
          </Form>
        )}
        <Box sx={{ height: '60vh' }}>
          {existingPickedIds && (
            <EntityList
              module={module}
              entity={entity}
              augFieldDefs={augFieldDefs}
              augRowsReq={augRowsReq}
              gridOptions={{ rowSelection: 'multiple' }}
              events={{ onRowSelected: rowSelected }}
              showTitle={false}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={submit} disabled={selectedEntities.length === 0}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
